import {
  ActionIcon,
  Alert,
  Button,
  Flex,
  Group,
  Modal,
  Text,
  TextInput,
} from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";
import { IconAlertCircle } from "@tabler/icons-react";
import { memo, useState } from "react";
import { MdEdit } from "react-icons/md";
import { successShowNotification } from "src/lib/mantine";
import { useChangeUserEmailMutation } from "src/service/AdminApi/AdminUsers";
import { z } from "zod";

const emailSchema = z
  .object({
    newEmail: z.string().email("有効なメールアドレスを入力してください"),
    confirmEmail: z.string().email("有効なメールアドレスを入力してください"),
  })
  .refine((data) => data.newEmail === data.confirmEmail, {
    message: "メールアドレスが一致しません",
    path: ["confirmEmail"],
  });

type EmailFormValues = z.infer<typeof emailSchema>;

interface EmailCellProps {
  userId: string;
  currentEmail: string | null;
  userName?: string;
  userNickname?: string;
  userRole?: string;
  onUpdate?: () => void;
}

const EmailCell = ({
  userId,
  currentEmail,
  userName,
  userNickname,
  userRole,
  onUpdate,
}: EmailCellProps) => {
  const [modalOpened, setModalOpened] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [changeUserEmail, { isLoading }] = useChangeUserEmailMutation();

  const form = useForm<EmailFormValues>({
    initialValues: {
      newEmail: "",
      confirmEmail: "",
    },
    validate: zodResolver(emailSchema),
  });

  const handleSubmit = async (values: EmailFormValues) => {
    setErrorMessage(null);
    try {
      await changeUserEmail({
        userId,
        newEmail: values.newEmail.trim().toLowerCase(),
        confirmEmail: values.confirmEmail.trim().toLowerCase(),
      }).unwrap();

      successShowNotification("メールアドレスを更新しました");
      setModalOpened(false);
      onUpdate?.();
    } catch (error: any) {
      console.error(error);
      const apiErrorMessage =
        error?.data?.error?.message ||
        error?.data?.message ||
        "メールアドレスの更新に失敗しました";
      setErrorMessage(apiErrorMessage);
    }
  };

  const handleModalClose = () => {
    setModalOpened(false);
    setErrorMessage(null);
    form.reset();
  };

  return (
    <>
      <Modal
        opened={modalOpened}
        onClose={handleModalClose}
        title="メールアドレス変更"
        size="md"
        centered
      >
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Flex direction="column" gap="md">
            {(userName || userNickname || currentEmail) && (
              <Flex
                direction="column"
                gap="xs"
                p="sm"
                style={{ backgroundColor: "#f8f9fa", borderRadius: "4px" }}
              >
                {userName && (
                  <Text size="sm" color="dark">
                    <Text component="span" weight={500}>
                      氏名:
                    </Text>{" "}
                    {userName}
                  </Text>
                )}
                {userNickname && (
                  <Text size="sm" color="dark">
                    <Text component="span" weight={500}>
                      ニックネーム:
                    </Text>{" "}
                    {userNickname}
                  </Text>
                )}
                {currentEmail && (
                  <Text size="sm" color="dark">
                    <Text component="span" weight={500}>
                      現在のメール:
                    </Text>{" "}
                    {currentEmail}
                  </Text>
                )}
                {userRole && (
                  <Text size="sm" color="dark">
                    <Text component="span" weight={500}>
                      役割:
                    </Text>{" "}
                    {userRole}
                  </Text>
                )}
                <Text size="xs" color="dark">
                  <Text component="span" weight={500}>
                    ID:
                  </Text>{" "}
                  {userId}
                </Text>
              </Flex>
            )}

            <TextInput
              label="新しいメールアドレス"
              placeholder="example@email.com"
              required
              {...form.getInputProps("newEmail")}
            />

            <TextInput
              label="メールアドレス確認"
              placeholder="example@email.com"
              required
              {...form.getInputProps("confirmEmail")}
            />

            {errorMessage && (
              <Alert
                icon={<IconAlertCircle size="1rem" />}
                title="エラー"
                color="red"
                variant="light"
              >
                {errorMessage}
              </Alert>
            )}

            <Group position="right">
              <Button
                variant="outline"
                onClick={handleModalClose}
                disabled={isLoading}
              >
                キャンセル
              </Button>
              <Button type="submit" loading={isLoading}>
                更新
              </Button>
            </Group>
          </Flex>
        </form>
      </Modal>

      <Flex align="center" gap="xs">
        <Text size="sm">{currentEmail || "-"}</Text>
        <ActionIcon
          size="sm"
          variant="subtle"
          onClick={() => setModalOpened(true)}
        >
          <MdEdit size={16} />
        </ActionIcon>
      </Flex>
    </>
  );
};

export const EmailCellMemo = memo(EmailCell);
